import { useSyncExternalStore } from "react";

// ONE shared minute clock for every relative-time label and cooldown on the
// page ("12 min ago", a "refresh stock now" button unlocking after its 5 min
// window): the first subscriber starts a single setInterval, the last one
// stops it, and the clock pauses while the tab is hidden - on return it ticks
// once right away so every label catches up instantly. A module store (same
// pattern as actAsFrameStore) instead of a timer per row: a logo list shows
// 100 rows, each with a label and a button.

export const MINUTE_TICK_MS = 60_000;

let tick = 0;
let interval: ReturnType<typeof setInterval> | undefined;
let watchingVisibility = false;
const listeners = new Set<() => void>();

function emit(): void {
  tick += 1;
  listeners.forEach((listener) => listener());
}

function hidden(): boolean {
  return typeof document !== "undefined" && document.visibilityState === "hidden";
}

function start(): void {
  if (interval != null || hidden()) return;
  interval = setInterval(emit, MINUTE_TICK_MS);
}

function stop(): void {
  if (interval == null) return;
  clearInterval(interval);
  interval = undefined;
}

function onVisibilityChange(): void {
  if (hidden()) {
    stop();
    return;
  }
  if (listeners.size === 0) return;
  emit();
  start();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  if (listeners.size === 1) {
    start();
    if (typeof document !== "undefined" && !watchingVisibility) {
      document.addEventListener("visibilitychange", onVisibilityChange);
      watchingVisibility = true;
    }
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size > 0) return;
    stop();
    if (watchingVisibility) {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      watchingVisibility = false;
    }
  };
}

function getSnapshot(): number {
  return tick;
}

export function getMinuteTickListenerCount(): number {
  return listeners.size;
}

export function resetMinuteTickForTests(): void {
  stop();
  listeners.clear();
  if (watchingVisibility) {
    document.removeEventListener("visibilitychange", onVisibilityChange);
    watchingVisibility = false;
  }
  tick = 0;
}

/**
 * Re-renders the component once a minute (and once when the tab becomes
 * visible again) so a label computed from Date.now() at render stays right.
 * Returns the tick count; most callers only need the re-render. Costs one
 * interval for the whole page, none while the tab is hidden.
 */
export function useMinuteTick(): number {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
